<template>
	<div id="clientContentList" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + $route.params.id" :title="client ? client.name : ''" :subtitle="cfg.title">
			<button class="hu-pill" @click="loadPages()" title="Reload" :disabled="loading">
				<font-awesome-icon :icon="['fas', 'sync-alt']" :spin="loading" />
				<span>Reload</span>
			</button>
		</JuniperPageHeader>

		<div v-if="cfg.heygov" class="jrd-intro mt-3">
			<span>Every {{ cfg.one }} of this website, loaded from HeyGov. Move {{ cfg.many }} to trash here, or open one in HeyGov to edit it.</span>
			<div class="jrd-intro__hint">
				<font-awesome-icon class="text-warning me-1" :icon="['fas', 'exclamation-triangle']" />
				Deleted {{ cfg.many }} are not listed and cannot be restored from MC2. {{ cfg.recurringHint }}
			</div>
		</div>
		<div v-else class="jrd-intro mt-3">
			<span>Every {{ cfg.one }} of this website, including soft-deleted ones. Restore, move to trash or delete {{ cfg.many }} permanently.</span>
			<div class="jrd-intro__hint">
				<font-awesome-icon class="text-danger me-1" :icon="['fas', 'exclamation-triangle']" />
				Permanent delete removes the {{ cfg.one }}, {{ cfg.related }} for good — it cannot be undone.
			</div>
		</div>

		<!-- Toolbar: tabs + filters -->
		<div class="cp-toolbar mt-3">
			<div v-if="cfg.heygov" class="cp-live-count">
				<b>{{ counts.active !== undefined ? counts.active : '–' }}</b> {{ cfg.many }}
			</div>
			<div v-else class="act-pills" role="group" :aria-label="'Show ' + cfg.many">
				<button
					v-for="tab in tabs"
					:key="tab.key"
					type="button"
					class="act-pill"
					:class="{ active: trash === tab.key }"
					:aria-pressed="trash === tab.key ? 'true' : 'false'"
					@click="setTab(tab.key)"
				>
					{{ tab.label }}
					<span class="cp-tab__count">{{ counts[tab.count] !== undefined ? counts[tab.count] : '–' }}</span>
				</button>
			</div>
			<div class="cp-filters">
				<select v-model="status" class="cp-input" @change="reload()" aria-label="Filter by status">
					<option value="">All statuses</option>
					<option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
				</select>
				<input v-model="search" type="search" class="cp-input cp-input--search" placeholder="Search title…" :aria-label="'Search ' + cfg.many" @input="onSearch" />
			</div>
		</div>

		<div class="ada-card mt-2">
			<div v-if="loading && !pages.length" class="act-empty">
				<span class="spinner-border spinner-border-sm me-2" role="status"></span>Loading…
			</div>
			<div v-else-if="!pages.length" class="act-empty">No {{ cfg.many }} found.</div>
			<div v-else class="act-table-wrap" :class="{ 'cp-dim': loading }">
				<table class="act-table">
					<thead>
						<tr>
							<th class="cp-check">
								<input type="checkbox" :checked="allSelected" :indeterminate.prop="someSelected" @change="toggleAll" aria-label="Select all on this page" />
							</th>
							<th v-for="col in columns" :key="col.key" :class="{ 'cp-sortable': col.sort }" @click="col.sort && sortBy(col.sort)">
								{{ col.label }}
								<font-awesome-icon v-if="col.sort && orderby === col.sort" :icon="['fas', order === 'asc' ? 'sort-up' : 'sort-down']" />
							</th>
							<th class="text-end">Actions</th>
						</tr>
					</thead>
					<tbody>
						<template v-if="cfg.heygov">
							<tr v-for="item in pages" :key="item.id">
								<td class="cp-check">
									<input type="checkbox" :value="item.id" v-model="selected" :aria-label="'Select ' + item.title" />
								</td>
								<td>
									<div class="cp-title">{{ item.title || '(no title)' }}</div>
									<div v-if="item.departments && item.departments.length" class="cp-sub">{{ item.departments.join(', ') }}</div>
								</td>
								<td>
									<span :class="['cp-pill', 'cp-pill--' + statusColor(item.status)]">{{ statusLabel(item.status) }}</span>
									<span v-if="item.recurring" class="cp-pill cp-pill--blue" :title="item.recurring === 'series' ? 'Repeating series' : 'One occurrence of a series'">
										<font-awesome-icon :icon="['fas', 'sync-alt']" class="me-1" />{{ item.recurring === 'series' ? 'Series' : 'Occurrence' }}
									</span>
								</td>
								<td class="cp-muted cp-kind">{{ kindLabel(item.kind) }}</td>
								<td class="cp-date">{{ formatDate(item.starts_at, true) }}</td>
								<td v-if="cfg.showUpdated" class="cp-date">{{ formatDate(item.updated_at) }}</td>
								<td class="text-end act-actions">
									<a v-if="heygovUrl" class="act-row-btn" :href="heygovUrl + cfg.editPath(item.id)" target="_blank" rel="noopener" title="Open in HeyGov">
										<font-awesome-icon :icon="['fas', 'external-link-alt']" />
									</a>
									<button class="act-row-btn act-row-btn--danger" @click="bulk('trash', [item.id])" title="Move to trash">
										<font-awesome-icon :icon="['fas', 'trash']" />
									</button>
								</td>
							</tr>
						</template>
						<template v-else>
						<tr v-for="page in pages" :key="page.id" :class="{ 'cp-row--deleted': isDeleted(page) }">
							<td class="cp-check">
								<input type="checkbox" :value="page.id" v-model="selected" :aria-label="'Select ' + page.title" />
							</td>
							<td class="cp-id">{{ page.id }}</td>
							<td>
								<div class="cp-title">{{ page.title || '(no title)' }}</div>
								<div v-if="cfg.departmentPages && page.is_department_page" class="jrd-url" title="Department page — its URL follows the department slug">/{{ page.slug_prefix || '' }}{{ page.slug }}</div>
								<button v-else type="button" class="jrd-url cp-slug-btn" title="Edit slug" :aria-label="'Edit slug /' + (page.slug_prefix || '') + page.slug" @click="openSlug(page)">
									/{{ page.slug_prefix || '' }}{{ page.slug }}
									<font-awesome-icon class="cp-slug-btn__icon" :icon="['fas', 'pen']" />
								</button>
							</td>
							<td>
								<span v-if="page.deleted_at" class="cp-pill cp-pill--red">Deleted</span>
								<span :class="['cp-pill', 'cp-pill--' + statusColor(page.status)]">{{ page.status }}</span>
								<font-awesome-icon v-if="page.pass" class="ms-1 text-muted" :icon="['fas', 'lock']" title="Password protected" />
							</td>
							<td class="cp-date">{{ formatDate(page.updated_at) }}</td>
							<td class="cp-date">{{ page.deleted_at ? formatDate(page.deleted_at) : '—' }}</td>
							<td class="cp-muted">{{ page.creator ? page.creator.name : '—' }}</td>
							<td class="text-end act-actions">
								<a v-if="!isDeleted(page) && page.status === 'publish' && siteUrl" class="act-row-btn" :href="pageUrl(page)" target="_blank" rel="noopener" title="View on website">
									<font-awesome-icon :icon="['fas', 'external-link-alt']" />
								</a>
								<button v-if="isDeleted(page)" class="act-row-btn" @click="bulk('restore', [page.id])" :title="'Restore ' + cfg.one">
									<font-awesome-icon :icon="['fas', 'undo']" />
								</button>
								<button v-else class="act-row-btn" @click="bulk('trash', [page.id])" title="Move to trash (soft delete)">
									<font-awesome-icon :icon="['fas', 'trash']" />
								</button>
								<button class="act-row-btn act-row-btn--danger" @click="bulk('force_delete', [page.id])" title="Delete permanently">
									<font-awesome-icon :icon="['fas', 'times-circle']" />
								</button>
							</td>
						</tr>
						</template>
					</tbody>
				</table>
			</div>

			<!-- Bulk bar: floats centred at the bottom of the table while rows are selected -->
			<transition name="cp-bulk-pop">
				<div v-if="selected.length" class="cp-bulk-dock">
					<div class="cp-bulk" role="toolbar" aria-label="Bulk actions">
						<span class="cp-bulk__count"><b>{{ selected.length }}</b> selected</span>
						<span class="cp-bulk__sep" aria-hidden="true"></span>
						<button v-if="cfg.heygov" type="button" class="cp-bulk-btn cp-bulk-btn--danger" :disabled="bulkLoading" :title="'Move ' + selected.length + ' to trash'" @click="bulk('trash', selected)">
							<font-awesome-icon :icon="['fas', 'trash']" />
							Move to trash<span class="cp-bulk-btn__n">{{ selected.length }}</span>
						</button>
						<template v-else>
						<button type="button" class="cp-bulk-btn" :disabled="bulkLoading || !selectedDeleted.length" :title="'Restore ' + selectedDeleted.length" @click="bulk('restore', selectedDeleted)">
							<font-awesome-icon :icon="['fas', 'undo']" />
							Restore<span v-if="selectedDeleted.length" class="cp-bulk-btn__n">{{ selectedDeleted.length }}</span>
						</button>
						<button type="button" class="cp-bulk-btn" :disabled="bulkLoading || !selectedActive.length" :title="'Move ' + selectedActive.length + ' to trash'" @click="bulk('trash', selectedActive)">
							<font-awesome-icon :icon="['fas', 'trash']" />
							Trash<span v-if="selectedActive.length" class="cp-bulk-btn__n">{{ selectedActive.length }}</span>
						</button>
						<button type="button" class="cp-bulk-btn cp-bulk-btn--danger" :disabled="bulkLoading" :title="'Delete ' + selected.length + ' permanently'" @click="bulk('force_delete', selected)">
							<font-awesome-icon :icon="['fas', 'times-circle']" />
							Delete permanently<span class="cp-bulk-btn__n">{{ selected.length }}</span>
						</button>
						</template>
						<button type="button" class="cp-bulk-close" title="Clear selection" aria-label="Clear selection" @click="selected = []">
							<font-awesome-icon :icon="['fas', 'times']" />
						</button>
					</div>
				</div>
			</transition>

			<div v-if="total" class="act-pagination">
				<div ref="perPageWrap" class="cp-perpage">
					<button
						type="button"
						class="cp-perpage__toggle"
						aria-haspopup="true"
						:aria-expanded="perPageOpen ? 'true' : 'false'"
						title="Rows per page"
						:aria-label="rangeLabel + ', rows per page'"
						@click="perPageOpen = !perPageOpen"
					>
						{{ rangeLabel }}
						<font-awesome-icon :icon="['fas', 'chevron-down']" class="cp-perpage__caret" />
					</button>
					<div v-if="perPageOpen" class="cp-perpage__menu" role="menu" @keydown.esc="perPageOpen = false">
						<div class="cp-perpage__title">Rows per page</div>
						<button
							v-for="opt in perPageOptions"
							:key="opt"
							type="button"
							role="menuitemradio"
							:aria-checked="perPage === opt ? 'true' : 'false'"
							:class="['cp-perpage__opt', { active: perPage === opt }]"
							@click="setPerPage(opt)"
						>
							{{ opt === 0 ? 'All' : opt }}
							<font-awesome-icon v-if="perPage === opt" :icon="['fas', 'check']" />
						</button>
					</div>
				</div>
				<div v-if="lastPage > 1" class="act-pagination__nav">
					<button type="button" class="act-page-link act-page-link--arrow" :disabled="loading || page <= 1" title="Previous page" aria-label="Previous page" @click="goTo(page - 1)">
						<font-awesome-icon :icon="['fas', 'chevron-left']" />
					</button>
					<template v-for="(p, i) in pageLinks">
						<span v-if="p === '...'" :key="'dots-' + i" class="act-page-dots">…</span>
						<button
							v-else
							:key="'p-' + p"
							type="button"
							class="act-page-link"
							:class="{ active: p === page }"
							:aria-current="p === page ? 'page' : null"
							:disabled="loading"
							@click="goTo(p)"
						>
							{{ p }}
						</button>
					</template>
					<button type="button" class="act-page-link act-page-link--arrow" :disabled="loading || page >= lastPage" title="Next page" aria-label="Next page" @click="goTo(page + 1)">
						<font-awesome-icon :icon="['fas', 'chevron-right']" />
					</button>
				</div>
			</div>
		</div>

		<ConfirmModal
			ref="confirmModal"
			:title="confirmTitle"
			:confirm-text="pendingAction ? labels[pendingAction].verb : 'Confirm'"
			:danger="pendingAction !== 'restore'"
			:busy="bulkLoading"
			@confirm="runBulk"
			@cancel="pendingAction = null"
		>
			<p class="mb-2"><b>{{ pendingNames }}</b></p>
			<template v-if="pendingAction === 'trash' && cfg.heygov">
				<p class="mb-2 cp-muted">Published {{ cfg.many }} are taken off the website. Deleted {{ cfg.many }} cannot be restored from MC2.</p>
				<p v-if="pendingSeries" class="mb-0 cp-muted"><b>{{ pendingSeries }}</b> {{ pendingSeries === 1 ? 'is a repeating series' : 'are repeating series' }}: {{ cfg.seriesTrash }}</p>
			</template>
			<p v-else-if="pendingAction === 'trash'" class="mb-0 cp-muted">Published {{ cfg.many }} are taken off the website. They can be restored later.</p>
			<template v-if="pendingAction === 'force_delete'">
				<p class="mb-2">Will be deleted for good, together with {{ cfg.relatedShort }}. <b>This cannot be undone.</b></p>
				<label class="cp-confirm-label" for="cpConfirmInput">Type <b>delete</b> to confirm</label>
				<input id="cpConfirmInput" v-model="confirmInput" type="text" :class="['cp-input', 'w-100', { 'cp-input--invalid': confirmError }]" autocomplete="off" @input="confirmError = false" @keyup.enter="runBulk()" />
				<div v-if="confirmError" class="cp-confirm-error">Type "delete" to confirm.</div>
			</template>
		</ConfirmModal>

		<ConfirmModal
			ref="slugModal"
			class="cp-slug-modal"
			title="Edit slug"
			icon-class="fa-link"
			confirm-text="Save"
			confirm-icon="fa-check"
			:busy="slugSaving"
			@confirm="saveSlug"
			@cancel="slugPage = null"
		>
			<template v-if="slugPage">
				<p class="mb-2"><b>{{ slugPage.title || '(no title)' }}</b></p>
				<label class="cp-confirm-label" for="cpSlugInput">Slug</label>
				<div class="cp-slug-field">
					<span class="cp-slug-field__prefix">/{{ slugPage.slug_prefix || '' }}</span>
					<input
						id="cpSlugInput"
						ref="slugInput"
						v-model="slugValue"
						type="text"
						:class="['cp-input', 'cp-slug-field__input', { 'cp-input--invalid': slugError }]"
						autocomplete="off"
						spellcheck="false"
						@input="slugError = ''"
						@blur="slugValue = slugify(slugValue)"
						@keyup.enter="saveSlug()"
					/>
				</div>
				<div v-if="slugError" class="cp-confirm-error">{{ slugError }}</div>
				<p v-else-if="slugPage.deleted_at" class="cp-slug-hint">This {{ cfg.one }} is deleted — renaming it frees the old slug for other {{ cfg.many }}.</p>
				<p v-else-if="slugPage.status === 'publish'" class="cp-slug-hint">The {{ cfg.one }} moves to the new URL on the website; the old URL stops working.</p>
			</template>
		</ConfirmModal>
	</div>
</template>

<script>
import juniperApi from '@/api/juniperApi'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import { showGlobalToast } from '@/utils/toast'

/**
 * One screen for every content type: the route picks the type through
 * meta.contentType ("pages" | "news" | "meetings" | "events"). Pages and news
 * share the Juniper all / bulk / update-slug endpoints; meetings and events are
 * the HeyGov ones (cfg.heygov) — live rows and bulk trash only.
 */
const CONTENT_TYPES = {
	pages: {
		endpoint: '/pages',
		title: 'Pages',
		one: 'page',
		many: 'pages',
		related: 'its revisions and SEO data',
		relatedShort: 'revisions and SEO data',
		slugTaken: 'another page or landing page',
		departmentPages: true,
		perPageKey: 'mc2.juniperClientPages.perPage',
	},
	news: {
		endpoint: '/news-notices',
		title: 'News & Notices',
		one: 'news item',
		many: 'news items',
		related: 'its category links and SEO data',
		relatedShort: 'category links and SEO data',
		slugTaken: 'another news item',
		departmentPages: false,
		perPageKey: 'mc2.juniperClientNews.perPage',
	},
	// Meetings and events live in heygov-api: juniper-api proxies its list and
	// DELETE endpoints (HeygovContentController). Live rows only — no deleted
	// tab, no restore, no permanent delete, no slugs. ids are HeyGov pids.
	meetings: {
		heygov: true,
		endpoint: '/heygov-content/meetings',
		title: 'Meetings',
		one: 'meeting',
		many: 'meetings',
		editPath: pid => `/meetings/${pid}`,
		kindLabel: 'Type',
		showUpdated: true,
		sortStatus: true,
		recurringHint: 'Trashing a series removes only that meeting — its occurrences stay.',
		seriesTrash: 'only the selected meetings go, their occurrences stay on the calendar.',
		perPageKey: 'mc2.juniperClientMeetings.perPage',
	},
	events: {
		heygov: true,
		endpoint: '/heygov-content/events',
		title: 'Events',
		one: 'event',
		many: 'events',
		editPath: pid => `/events/${pid}/edit`,
		kindLabel: 'Privacy',
		showUpdated: false,
		sortStatus: false,
		recurringHint: 'Trashing a repeating event removes all of its occurrences too.',
		seriesTrash: 'all of their occurrences are deleted too.',
		perPageKey: 'mc2.juniperClientEvents.perPage',
	},
}

const PER_PAGE_OPTIONS = [10, 20, 50, 100, 0] // 0 = All
// heygov-api clamps limit to 200 and has no "all"
const HEYGOV_PER_PAGE_OPTIONS = [10, 20, 50, 100, 200]
// most ids one bulk call takes (juniper-api validation)
const BULK_CHUNK = { juniper: 500, heygov: 200 }

const STATUS_OPTIONS = {
	juniper: [
		{ value: 'publish', label: 'Published' },
		{ value: 'draft', label: 'Draft' },
		{ value: 'future', label: 'Scheduled' },
		{ value: 'private', label: 'Private' },
		{ value: 'archived', label: 'Archived' },
		{ value: 'trashed', label: 'Trashed (legacy status)' },
	],
	heygov: [
		{ value: 'publish', label: 'Published' },
		{ value: 'draft', label: 'Draft' },
		{ value: 'canceled', label: 'Canceled' },
	],
}

function contentConfig(route) {
	return CONTENT_TYPES[route.meta && route.meta.contentType] || CONTENT_TYPES.pages
}

function perPageOptionsFor(cfg) {
	return cfg.heygov ? HEYGOV_PER_PAGE_OPTIONS : PER_PAGE_OPTIONS
}

function readPerPage(cfg) {
	try {
		const saved = parseInt(localStorage.getItem(cfg.perPageKey), 10)
		if (perPageOptionsFor(cfg).includes(saved)) return saved
	} catch (e) {
		// storage blocked — fall back to the default
	}
	return cfg.heygov ? 20 : 10
}

const BULK_LABELS = {
	restore: { verb: 'Restore', title: n => `Restore ${n}?`, done: 'restored' },
	trash: { verb: 'Move to trash', title: n => `Move ${n} to trash?`, done: 'moved to trash' },
	force_delete: { verb: 'Delete permanently', title: n => `Permanently delete ${n}?`, done: 'permanently deleted' },
}

export default {
	name: 'ClientContentList',
	components: { ConfirmModal },
	data() {
		return {
			labels: BULK_LABELS,
			pendingAction: null,
			pendingIds: [],
			confirmInput: '',
			confirmError: false,
			slugPage: null,
			slugValue: '',
			slugError: '',
			slugSaving: false,
			client: null,
			pages: [],
			counts: {},
			total: 0,
			page: 1,
			perPage: readPerPage(contentConfig(this.$route)),
			perPageOpen: false,
			heygovUrl: '',
			trash: 'with',
			status: '',
			search: '',
			orderby: '',
			order: 'desc',
			selected: [],
			loading: false,
			bulkLoading: false,
			searchTimer: null,
			tabs: [
				{ key: 'with', label: 'All', count: 'total' },
				{ key: 'without', label: 'Active', count: 'active' },
				{ key: 'only', label: 'Deleted', count: 'deleted' },
			],
		}
	},
	computed: {
		cfg() {
			return contentConfig(this.$route)
		},
		perPageOptions() {
			return perPageOptionsFor(this.cfg)
		},
		statusOptions() {
			return STATUS_OPTIONS[this.cfg.heygov ? 'heygov' : 'juniper']
		},
		columns() {
			if (this.cfg.heygov) {
				return [
					{ key: 'title', label: 'Title', sort: 'title' },
					{ key: 'status', label: 'Status', sort: this.cfg.sortStatus ? 'status' : null },
					{ key: 'kind', label: this.cfg.kindLabel },
					{ key: 'starts', label: 'Starts', sort: 'starts_at' },
					...(this.cfg.showUpdated ? [{ key: 'updated', label: 'Updated', sort: 'updated_at' }] : []),
				]
			}
			return [
				{ key: 'id', label: 'ID', sort: 'id' },
				{ key: 'title', label: 'Title', sort: 'title' },
				{ key: 'status', label: 'Status', sort: 'status' },
				{ key: 'updated', label: 'Updated', sort: 'updated_at' },
				{ key: 'deleted', label: 'Deleted', sort: 'deleted_at' },
				{ key: 'creator', label: 'Created by' },
			]
		},
		// selected repeating series in the pending action (their trash differs per type)
		pendingSeries() {
			return this.pendingIds.filter(id => this.pagesById[id] && this.pagesById[id].recurring === 'series').length
		},
		pagesById() {
			return Object.fromEntries(this.pages.map(p => [p.id, p]))
		},
		selectedDeleted() {
			return this.selected.filter(id => this.pagesById[id] && this.isDeleted(this.pagesById[id]))
		},
		selectedActive() {
			return this.selected.filter(id => this.pagesById[id] && !this.pagesById[id].deleted_at)
		},
		allSelected() {
			return this.pages.length > 0 && this.pages.every(p => this.selected.includes(p.id))
		},
		someSelected() {
			return this.selected.length > 0 && !this.allSelected
		},
		lastPage() {
			return this.perPage ? Math.max(1, Math.ceil(this.total / this.perPage)) : 1
		},
		rangeLabel() {
			if (!this.perPage) return `All ${this.total}`
			const from = (this.page - 1) * this.perPage + 1
			const to = Math.min(this.page * this.perPage, this.total)
			return `${from}–${to} of ${this.total}`
		},
		// Windowed page list with ellipsis, same as Activities: 1 … 4 5 6 7 8 … 20
		pageLinks() {
			const out = []
			let prev = 0
			for (let i = 1; i <= this.lastPage; i++) {
				if (i === 1 || i === this.lastPage || (i >= this.page - 2 && i <= this.page + 2)) {
					if (prev && i - prev > 1) out.push('...')
					out.push(i)
					prev = i
				}
			}
			return out
		},
		confirmTitle() {
			if (!this.pendingAction) return ''
			const n = this.pendingIds.length
			return BULK_LABELS[this.pendingAction].title(n === 1 ? '1 ' + this.cfg.one : n + ' ' + this.cfg.many)
		},
		pendingNames() {
			return this.titlesOf(this.pendingIds)
		},
		siteUrl() {
			if (!this.client) return ''
			const base = this.client.staging && this.client.staging_url ? this.client.staging_url : this.client.url
			return (base || '').replace(/\/+$/, '')
		},
	},
	watch: {
		// pages ↔ news reuse this instance: start over with the other type
		'$route.meta.contentType'() {
			this.perPage = readPerPage(this.cfg)
			this.heygovUrl = ''
			this.trash = 'with'
			this.status = ''
			this.search = ''
			this.orderby = ''
			this.pages = []
			this.counts = {}
			this.reload()
		},
	},
	created() {
		this.getClient()
		this.loadPages()
	},
	mounted() {
		document.addEventListener('mousedown', this.onOutsideClick)
	},
	beforeDestroy() {
		clearTimeout(this.searchTimer)
		document.removeEventListener('mousedown', this.onOutsideClick)
	},
	methods: {
		setPerPage(value) {
			this.perPageOpen = false
			if (value === this.perPage) return
			this.perPage = value
			try {
				localStorage.setItem(this.cfg.perPageKey, String(value))
			} catch (e) {
				// storage blocked — the choice just lasts for this visit
			}
			this.reload()
		},
		onOutsideClick(e) {
			if (this.perPageOpen && this.$refs.perPageWrap && !this.$refs.perPageWrap.contains(e.target)) {
				this.perPageOpen = false
			}
		},
		getClient() {
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => (this.client = data))
				.catch(error => console.log(error))
		},
		loadPages() {
			this.loading = true
			const params = { client_id: this.$route.params.id }
			if (!this.cfg.heygov) params.trash = this.trash
			// perPage 0 = All: no page/per_page, the API then returns every row
			if (this.perPage) {
				params.page = this.page
				params.per_page = this.perPage
			}
			if (this.status) params.status = this.status
			if (this.search.trim()) params.search = this.search.trim()
			if (this.orderby) {
				params.orderby = this.orderby
				params.order = this.order
			}
			return juniperApi
				.get(this.cfg.endpoint + '/all', { params })
				.then(({ data, headers }) => {
					// pages come as a plain array, news as { post_data, posts, page }
					const rows = Array.isArray(data) ? data : data.post_data || []
					this.pages = rows
					this.total = parseInt(headers['x-wp-total'] || (Array.isArray(data) ? rows.length : data.posts) || 0, 10)
					try {
						this.counts = JSON.parse(headers['x-page-counts'] || '{}')
					} catch (e) {
						this.counts = {}
					}
					if (headers['x-heygov-url']) this.heygovUrl = headers['x-heygov-url']
					// keep only selections that are still visible
					this.selected = this.selected.filter(id => this.pagesById[id])
				})
				.catch(error => {
					showGlobalToast((error.response && error.response.data && error.response.data.message) || error.message, 'error', 6000)
				})
				.finally(() => (this.loading = false))
		},
		reload() {
			this.page = 1
			this.selected = []
			this.loadPages()
		},
		setTab(key) {
			if (this.trash === key) return
			this.trash = key
			this.reload()
		},
		onSearch() {
			clearTimeout(this.searchTimer)
			this.searchTimer = setTimeout(() => this.reload(), 350)
		},
		sortBy(column) {
			if (this.orderby === column) {
				this.order = this.order === 'asc' ? 'desc' : 'asc'
			} else {
				this.orderby = column
				this.order = column === 'title' ? 'asc' : 'desc'
			}
			this.reload()
		},
		goTo(page) {
			this.page = page
			this.selected = []
			this.loadPages()
		},
		toggleAll(e) {
			this.selected = e.target.checked ? this.pages.map(p => p.id) : []
		},
		// Soft-deleted, or a legacy row stamped status 'trashed' — both restorable.
		isDeleted(page) {
			return !!page.deleted_at || page.status === 'trashed'
		},
		statusColor(status) {
			return { publish: 'green', draft: 'gray', future: 'blue', private: 'purple', archived: 'orange', canceled: 'orange', trashed: 'red' }[status] || 'gray'
		},
		statusLabel(status) {
			return { publish: 'Published', draft: 'Draft', canceled: 'Canceled' }[status] || status || '—'
		},
		// meeting type (regular, work_session…) or event privacy
		kindLabel(kind) {
			return kind ? kind.replace(/_/g, ' ') : '—'
		},
		pageUrl(page) {
			return `${this.siteUrl}/${page.slug_prefix || ''}${page.slug}`
		},
		// local = a town wall time without zone ("Y-m-d H:i:s", HeyGov starts_at):
		// read and shown as UTC so it is not shifted into the viewer's timezone
		formatDate(value, local = false) {
			if (!value) return '—'
			const d = new Date(local ? String(value).replace(' ', 'T') + 'Z' : value)
			if (isNaN(d)) return value
			return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', ...(local ? { timeZone: 'UTC' } : {}) })
		},
		titlesOf(ids) {
			const titles = ids.map(id => this.pagesById[id]?.title || `#${id}`)
			const shown = titles.slice(0, 5)
			const list = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(
				titles.length > 5 ? [...shown, `${titles.length - 5} more`] : shown
			)
			return list
		},
		openSlug(page) {
			this.slugPage = page
			this.slugValue = page.slug || ''
			this.slugError = ''
			this.$refs.slugModal.open()
			// focus once the modal is shown (bootstrap fade)
			setTimeout(() => this.$refs.slugInput && this.$refs.slugInput.select(), 300)
		},
		// Same shape the CMS uses for slugs: lowercase, a-z0-9 and dashes.
		slugify(value) {
			return (value || '')
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim()
				.replace(/[^a-z0-9-]+/g, '-')
				.replace(/-{2,}/g, '-')
				.replace(/^-+|-+$/g, '')
		},
		async saveSlug() {
			const page = this.slugPage
			if (!page || this.slugSaving) return
			const slug = this.slugify(this.slugValue)
			this.slugValue = slug
			if (!slug) {
				this.slugError = 'Slug cannot be empty.'
				return
			}
			if (slug === page.slug) {
				this.$refs.slugModal.close()
				return
			}
			this.slugSaving = true
			try {
				// The endpoint answers with the slug the item ends up with: the old
				// one back means another page or landing page already uses it.
				const { data } = await juniperApi.put(`${this.cfg.endpoint}/${page.id}/update-slug`, { client_id: Number(this.$route.params.id), slug })
				if (data !== slug) {
					this.slugError = `"${slug}" is already used by ${this.cfg.slugTaken} (deleted ones included).`
					return
				}
				page.slug = data
				showGlobalToast('Slug updated', 'success')
				this.$refs.slugModal.close()
			} catch (error) {
				const errors = error.response && error.response.data && error.response.data.errors
				this.slugError = (errors && errors.slug && errors.slug[0]) || (error.response && error.response.data && error.response.data.message) || error.message
			} finally {
				this.slugSaving = false
			}
		},
		// Row/bulk buttons land here: stash the action and ask first.
		bulk(action, ids) {
			if (!ids.length) return
			this.pendingAction = action
			this.pendingIds = [...ids]
			this.confirmInput = ''
			this.confirmError = false
			this.$refs.confirmModal.open()
		},
		async runBulk() {
			const action = this.pendingAction
			const ids = this.pendingIds
			if (!action || !ids.length || this.bulkLoading) return
			if (action === 'force_delete' && this.confirmInput.trim().toLowerCase() !== 'delete') {
				this.confirmError = true
				return
			}
			this.bulkLoading = true
			try {
				// the API takes a limited number of ids per call — "All" can select more
				const chunk = BULK_CHUNK[this.cfg.heygov ? 'heygov' : 'juniper']
				const data = { done: [], failed: [], missing: [] }
				for (let i = 0; i < ids.length; i += chunk) {
					const res = await juniperApi.post(this.cfg.endpoint + '/bulk', { client_id: Number(this.$route.params.id), action, ids: ids.slice(i, i + chunk) })
					data.done.push(...(res.data.done || []))
					data.failed.push(...(res.data.failed || []))
					data.missing.push(...(res.data.missing || []))
				}
				const failed = (data.failed || []).length + (data.missing || []).length
				const done = data.done.length
				showGlobalToast(
					`${done} ${done === 1 ? this.cfg.one : this.cfg.many} ${BULK_LABELS[action].done}` + (failed ? ` — ${failed} could not be processed` : ''),
					failed ? 'warning' : 'success'
				)
				this.selected = this.selected.filter(id => !data.done.includes(id))
				this.$refs.confirmModal.close()
				await this.loadPages()
				// last page emptied by the action → step back
				if (!this.pages.length && this.page > 1) this.goTo(this.page - 1)
			} catch (error) {
				showGlobalToast((error.response && error.response.data && error.response.data.message) || error.message, 'error', 6000)
			} finally {
				this.bulkLoading = false
			}
		},
	},
}
</script>

<style scoped>
#clientContentList {
	padding: 0 0 60px;
	margin-top: 0 !important;
}
.jc-view {
	width: 100%;
}
.hu-pill {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 7px 12px;
	border: 1px solid #e0e1e1;
	border-radius: 999px;
	background: #ffffff;
	color: #64748b;
	font-size: 12px;
	font-weight: 600;
	white-space: nowrap;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.hu-pill:hover:not(:disabled) {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
}
.hu-pill:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}
.hu-pill svg {
	font-size: 12px;
}
.hu-pill--danger {
	color: #dc3545;
	border-color: #f5c2c7;
}
.hu-pill--danger:hover:not(:disabled) {
	background: #fdecee;
	border-color: #dc3545;
	color: #b02a37;
}

.jrd-intro {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 12px 14px;
	font-size: 13px;
	color: #4b5563;
}
.jrd-intro__hint {
	margin-top: 8px;
	font-size: 12px;
	color: #6b7280;
}

/* Toolbar */
.cp-toolbar {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	align-items: center;
	justify-content: space-between;
}
.act-pills {
	display: flex;
	background: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}
.act-pill {
	background: none;
	border: none;
	padding: 5px 14px;
	font-size: 13px;
	font-weight: 600;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.15s ease;
}
.act-pill.active {
	background: #fff;
	color: #111827;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.act-pill:hover:not(.active) {
	color: #374151;
}
.cp-tab__count {
	margin-left: 6px;
	font-weight: 500;
	opacity: 0.75;
}
.cp-filters {
	display: flex;
	gap: 8px;
}
.cp-input {
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	font-size: 13px;
	padding: 6px 10px;
	background: #fff;
}
.cp-input:focus {
	outline: none;
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}
.cp-input--invalid {
	border-color: #dc3545;
}
.cp-confirm-label {
	display: block;
	font-size: 12px;
	color: #374151;
	margin-bottom: 4px;
}
.cp-confirm-error {
	margin-top: 4px;
	font-size: 12px;
	color: #dc3545;
}
.cp-input--search {
	width: 220px;
}

/* Bulk bar — compact floating toolbar, centred over the bottom of the table.
   The dock is a zero-height sticky line (takes no space, the card does not
   grow); the bar hangs above it, so it overlaps the last rows and follows the
   viewport bottom while a long table is scrolled. */
.cp-bulk-dock {
	position: sticky;
	bottom: 16px;
	z-index: 5;
	height: 0;
	pointer-events: none;
}
.cp-bulk {
	pointer-events: auto;
	position: absolute;
	left: 50%;
	bottom: 6px;
	transform: translateX(-50%);
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 4px 4px 4px 12px;
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 999px;
	box-shadow: 0 6px 20px rgba(17, 24, 39, 0.12), 0 1px 3px rgba(17, 24, 39, 0.08);
	font-size: 12px;
	color: #374151;
}
.cp-bulk__count {
	white-space: nowrap;
}
.cp-bulk__sep {
	width: 1px;
	height: 16px;
	margin: 0 4px;
	background: #e6e8ec;
}
.cp-bulk-btn {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 4px 9px;
	border: none;
	border-radius: 999px;
	background: transparent;
	color: #374151;
	font-size: 12px;
	font-weight: 600;
	white-space: nowrap;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
}
.cp-bulk-btn svg {
	font-size: 11px;
}
.cp-bulk-btn:hover:not(:disabled) {
	background: #f3f5f9;
}
.cp-bulk-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}
.cp-bulk-btn--danger {
	color: #dc3545;
}
.cp-bulk-btn--danger:hover:not(:disabled) {
	background: #fdecee;
	color: #b02a37;
}
.cp-bulk-btn__n {
	min-width: 16px;
	padding: 0 5px;
	border-radius: 999px;
	background: #f3f4f6;
	font-size: 10px;
	line-height: 16px;
	text-align: center;
}
.cp-bulk-btn--danger .cp-bulk-btn__n {
	background: #fee2e2;
}
.cp-bulk-close {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	margin-left: 2px;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: #9ca3af;
	font-size: 11px;
	cursor: pointer;
}
.cp-bulk-close:hover {
	background: #f3f5f9;
	color: #374151;
}
.cp-bulk-pop-enter-active,
.cp-bulk-pop-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
}
.cp-bulk-pop-enter,
.cp-bulk-pop-leave-to {
	opacity: 0;
	transform: translateY(6px);
}
.cp-bulk {
	white-space: nowrap;
}

/* Card + table */
.ada-card {
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 1rem 1.1rem;
}
.act-empty {
	text-align: center;
	padding: 1.5rem 1rem;
	color: #8a909c;
	font-size: 0.9rem;
}
.act-table-wrap {
	overflow-x: auto;
	transition: opacity 0.15s ease;
}
.cp-dim {
	opacity: 0.55;
	pointer-events: none;
}
.act-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}
.act-table thead th {
	text-align: left;
	font-weight: 600;
	color: #6c757d;
	padding: 4px 10px 8px 0;
	border-bottom: 1px solid #dee2e6;
	white-space: nowrap;
}
.cp-sortable {
	cursor: pointer;
	user-select: none;
}
.cp-sortable:hover {
	color: #111827;
}
.act-table tbody td {
	padding: 8px 10px 8px 0;
	border-bottom: 1px solid #f1f3f5;
	vertical-align: middle;
	color: #2b303b;
}
.act-table tbody tr:last-child td {
	border-bottom: none;
}
.act-table tbody tr:hover td {
	background: #f3f5f9;
}
.cp-row--deleted td {
	background: #fff8f8;
}
.cp-row--deleted .cp-title {
	color: #6b7280;
}
.cp-check {
	width: 28px;
}
.cp-id {
	color: #9ca3af;
	font-variant-numeric: tabular-nums;
}
.cp-title {
	font-weight: 600;
}
.cp-sub {
	font-size: 11px;
	color: #6b7280;
}
.cp-kind {
	text-transform: capitalize;
}
.cp-live-count {
	font-size: 13px;
	color: #6b7280;
}
.cp-slug-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 0;
	border: none;
	background: none;
	text-align: left;
	cursor: pointer;
	border-radius: 4px;
}
.cp-slug-btn:hover,
.cp-slug-btn:focus-visible {
	color: #2b6cff;
	text-decoration: underline;
}
.cp-slug-btn__icon {
	font-size: 9px;
	opacity: 0;
	transition: opacity 0.15s ease;
}
.cp-slug-btn:hover .cp-slug-btn__icon,
.cp-slug-btn:focus-visible .cp-slug-btn__icon {
	opacity: 1;
}
/* Slug modal: twice the ConfirmModal width (modal-sm 300px) — prefixes + slugs are long */
.cp-slug-modal ::v-deep .modal-dialog {
	max-width: 600px;
}
.cp-slug-modal ::v-deep .cp-slug-field__prefix {
	max-width: 60%;
}
.cp-slug-field {
	display: flex;
	align-items: center;
	border: 1px solid #e6e8ec;
	border-radius: 8px;
	overflow: hidden;
}
.cp-slug-field__prefix {
	padding: 6px 2px 6px 10px;
	font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
	color: #9ca3af;
	white-space: nowrap;
	max-width: 50%;
	overflow: hidden;
	text-overflow: ellipsis;
}
.cp-slug-field__input {
	flex: 1;
	min-width: 0;
	border: none;
	border-radius: 0;
	padding-left: 0;
	font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
	font-size: 12px;
}
.cp-slug-field__input:focus {
	box-shadow: none;
}
.cp-slug-field:focus-within {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
}
.cp-slug-hint {
	margin: 6px 0 0;
	font-size: 12px;
	color: #6b7280;
}
.jrd-url {
	font-family: 'SF Mono', Menlo, Monaco, Consolas, 'Courier New', monospace;
	font-size: 11px;
	color: #6b7280;
	word-break: break-word;
}
.cp-date {
	white-space: nowrap;
	color: #4b5563;
}
.cp-muted {
	color: #6b7280;
}

.cp-pill {
	display: inline-block;
	padding: 2px 8px;
	margin-right: 4px;
	border-radius: 999px;
	font-size: 11px;
	font-weight: 600;
	text-transform: capitalize;
}
.cp-pill--green { background: #dcfce7; color: #15803d; }
.cp-pill--gray { background: #f3f4f6; color: #4b5563; }
.cp-pill--blue { background: #dbeafe; color: #1d4ed8; }
.cp-pill--purple { background: #ede9fe; color: #6d28d9; }
.cp-pill--orange { background: #ffedd5; color: #c2410c; }
.cp-pill--red { background: #fee2e2; color: #b91c1c; }

.act-actions {
	white-space: nowrap;
}
.act-row-btn {
	display: inline-block;
	background: none;
	border: none;
	color: #adb5bd;
	padding: 4px 6px;
	border-radius: 6px;
	cursor: pointer;
	font-size: 13px;
	line-height: 1;
	transition: color 0.15s ease, background 0.15s ease;
}
.act-row-btn + .act-row-btn {
	margin-left: 2px;
}
.act-row-btn:hover {
	color: #2b303b;
	background: #f3f5f9;
}
.act-row-btn--danger:hover {
	color: #dc3545;
	background: #fdecee;
}

/* Rows-per-page popover on the range label */
.cp-perpage {
	position: relative;
}
.cp-perpage__toggle {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	padding: 2px 6px;
	margin-left: -6px;
	border: none;
	border-radius: 6px;
	background: none;
	color: #8a909c;
	font-size: 0.82rem;
	font-variant-numeric: tabular-nums;
	cursor: pointer;
}
.cp-perpage__toggle:hover,
.cp-perpage__toggle[aria-expanded='true'] {
	background: #f3f5f9;
	color: #2b303b;
}
.cp-perpage__caret {
	font-size: 9px;
}
.cp-perpage__menu {
	position: absolute;
	left: -6px;
	bottom: calc(100% + 6px);
	z-index: 10;
	min-width: 140px;
	padding: 4px;
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 10px;
	box-shadow: 0 6px 20px rgba(17, 24, 39, 0.12), 0 1px 3px rgba(17, 24, 39, 0.08);
}
.cp-perpage__title {
	padding: 4px 8px 6px;
	font-size: 11px;
	font-weight: 600;
	color: #8a909c;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}
.cp-perpage__opt {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 5px 8px;
	border: none;
	border-radius: 6px;
	background: none;
	color: #374151;
	font-size: 13px;
	text-align: left;
	cursor: pointer;
}
.cp-perpage__opt:hover {
	background: #f3f5f9;
}
.cp-perpage__opt.active {
	font-weight: 700;
	color: #111827;
}
.cp-perpage__opt svg {
	font-size: 10px;
}

/* Pagination — mirrors Activities: range on the left, page links on the right */
.act-pagination {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0.85rem 0 0.25rem;
	font-size: 0.82rem;
}
.act-muted {
	color: #8a909c;
}
.act-pagination__nav {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}
.act-page-link {
	background: none;
	border: 0;
	padding: 0 4px;
	font-size: 0.82rem;
	font-variant-numeric: tabular-nums;
	color: #8a909c;
	cursor: pointer;
	transition: color 0.12s ease;
}
.act-page-link:hover:not(:disabled),
.act-page-link.active {
	color: #2b303b;
}
.act-page-link.active {
	font-weight: 700;
}
.act-page-link:disabled {
	opacity: 0.35;
	cursor: not-allowed;
}
.act-page-link.active:disabled {
	opacity: 1;
}
.act-page-link--arrow {
	font-size: 0.72rem;
}
.act-page-dots {
	color: #8a909c;
	padding: 0 2px;
	user-select: none;
}
</style>
